import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOfflineTransactionRequest,
  verifyOfflineTransactionRequest,
  cancelOfflineTransactionRequest,
} from "../../../redux/OfflineTransaction/OfflineTSlice";
import TableCompo from "../Table/TableCompo";
import { toast } from "react-toastify";
import Sidebar from "../../Drawer/Sidebar";
import {
  Box,
  Typography,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  useMediaQuery, useTheme
} from "@mui/material";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import useCsvConverter from "../../../hooks/convertToCSV";
import { canShowPermission } from "../Permissions/permissionObject";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const OfflineTransaction = () => {
  const dispatch = useDispatch();
  const offlineTransaction = useSelector(
    (state) => state.offlineTransaction || {},
  );
  const { data = [], total = 0 } = offlineTransaction;
  const decimal = useSelector(
    (state) => state?.appConfig?.configData?.decimalPrecision || 4,
  );
  const package_name = useSelector(
    (state) => state?.appConfig?.configData?.package_name || "",
  );

  const [limit, setLimit] = useState(5);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [cancelReason, setCancelReason] = useState("");
  const [cancelDialog, setCancelDialog] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [permissions, setPermissions] = useState({});

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("userPermissions");
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch (e) {
        console.error("permission parse error", e);
      }
    }
  }, []);
  const canEdit = canShowPermission(permissions, "offlineTransaction", "edit");

  const formatDateTimeRange = (date, type) => {
  if (!date) return null;
  const d = new Date(date);
  if (type === "start") {
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  if (type === "end") {
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
};

  useEffect(() => {
    const payload = { start: 0, limit, search };
    if (startDate && endDate) {
      payload.startDate = formatDateTimeRange(startDate, "start");
      payload.endDate = formatDateTimeRange(endDate, "end");
    }
    dispatch(fetchOfflineTransactionRequest(payload));
  }, [dispatch, limit, search,]);

  const handleGetDetails = () => {
  if (!startDate || !endDate) {
    toast.error("Please select both start and end dates.");
    return;
  }
  if (startDate > endDate) {
    toast.error("Start Date cannot be after End Date");
    return;
  }

  setPage(0);
  dispatch(
    fetchOfflineTransactionRequest({
      start: 0,
      limit,
      search,
      startDate: formatDateTimeRange(startDate, "start"),
      endDate: formatDateTimeRange(endDate, "end"),
    })
  );
};

  const handleStatusClick = (row, status) => {
    if (status === "SUCCESS") {
      const transactionId = row?.transactions?.[0]?._id;
      if (transactionId) {
        const invoiceUrl = `${window.location.origin}/invoice/${transactionId}`;
        window.open(invoiceUrl, "_blank");
      } else {
        toast.error("Transaction ID not found");
      }
    } else if (status === "PENDING") {
      setSelectedRow(row);
      setOpenDialog(true);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedRow(null);
  };

  const handleApprove = () => {
    if (selectedRow) {
      dispatch(
        verifyOfflineTransactionRequest({
          id: selectedRow?.order?._id,
        }),
      );
    }
    handleDialogClose();
  };

  const handleCancel = () => {
    setCancelDialog(true);
    setOpenDialog(false);
  };

  const handleCancelConfirm = () => {
    if (!cancelReason) {
      toast.error("Reason is required");
      return;
    }
    if (selectedRow) {
      dispatch(
        cancelOfflineTransactionRequest({
          id: selectedRow.order?._id,
          remark: cancelReason || "No reason provided",
        }),
      );
    }
    setCancelDialog(false);
    setSelectedRow(null);
    setCancelReason("");
  };

  const handleCancelDialogClose = () => {
    setCancelDialog(false);
    setCancelReason("");
  };

  const handleImageDialogOpen = (imageUrl) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };

  const handleImageDialogClose = () => {
    setImageDialogOpen(false);
    setSelectedImage("");
  };

  const handleImageDownload = () => {
    if (!selectedImage) return;

    const link = document.createElement("a");
    link.href = selectedImage;
    link.download = "offline-transaction-document";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTableChange = (action, tableState) => {
    const dateParams =
    startDate && endDate
      ? {
          startDate: formatDateTimeRange(startDate, "start"),
          endDate: formatDateTimeRange(endDate, "end"),
        }
      : {};
    switch (action) {
      case "changePage":
        setPage(tableState.page);
        dispatch(
          fetchOfflineTransactionRequest({
            start: tableState.page * tableState.rowsPerPage,
            limit: tableState.rowsPerPage,
            search,
            ...(startDate && endDate
            ? {
                startDate: formatDateTimeRange(startDate, "start"),
                endDate: formatDateTimeRange(endDate, "end"),
              }
            : {}),
          }),
        );
        break;

      case "changeRowsPerPage":
        setLimit(tableState.rowsPerPage);
        setPage(0);
        dispatch(
          fetchOfflineTransactionRequest({
            start: 0,
            limit: tableState.rowsPerPage,
            search,
            ...dateParams,
          }),
        );
        break;

      case "search":
        const searched = tableState.searchText || "";
        setSearch(searched);
        setPage(0);
        dispatch(
          fetchOfflineTransactionRequest({
            start: 0,
            limit,
            search: searched,
            ...dateParams,
          }),
        );
        break;

      default:
        break;
    }
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Created At",
      render: (val, row) => {
        const createdAt = row.createdAt;
        return createdAt
          ? new Date(createdAt).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : "N.A";
      },
    },
    {
      field: "customerName",
      headerName: "Name",
      render: (val, row) =>
        row.customer
          ? `${row.customer.firstName || ""} ${row.customer.middleName || ""} ${row.customer.lastName || ""}`
          : "N.A",
    },
    { field: "MetalName", headerName: "Metal" },
    {
      field: "order.weight",
      headerName: "Weight",
      render: (val, row) => Number(row.order?.weight).toFixed(decimal) || "N.A",
    },
    {
      field: "order.amount",
      headerName: "Amount",
      render: (val, row) => Number(row.order?.amount).toFixed(2) || "N.A",
    },
    {
      field: "id",
      headerName: "Transaction Details",
      render: (val, row) =>
        `TransactionId : ${row?.transactionId ? row.transactionId : "N.A"}`,
    },
    {
      field: "document",
      headerName: "Document",
      render: (val, row) => {
        if (!row?.document) return "N.A";

        return (
          <Button
            variant="text"
            size="small"
            onClick={() => handleImageDialogOpen(row.document)}
          >
            View Image
          </Button>
        );
      },
    },
    {
      field: "transactions[0].verifiedBy",
      headerName: "Verify By",
      render: (val, row) => row.transactions[0]?.verifiedBy || "N.A",
    },
    { field: "remark", headerName: "Remark" },
    {
      field: "status",
      headerName: "Action",
      render: (val, row) => {
        const status = row.status;
        switch (status) {
          case "PENDING":
            return (
              <span
                style={{
                  color: "#f0ad4e",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
                onClick={() => canEdit && handleStatusClick(row, "PENDING")}
              >
                PENDING
              </span>
            );
          case "SUCCESS":
            return (
              <span
                style={{
                  color: "green",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
                onClick={() => canEdit && handleStatusClick(row, "SUCCESS")}
              >
                SUCCESS
              </span>
            );
          case "CANCELLED":
            return (
              <span
                style={{ color: "red", cursor: "pointer", fontWeight: "bold" }}
                onClick={() => canEdit && handleStatusClick(row, "CANCELLED")}
              >
                CANCELLED
              </span>
            );
          default:
            return <span>{status || "N.A"}</span>;
        }
      },
    },
  ];

  const handleExportReport = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end date");
      return;
    }
    setExporting(true);
    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/transactions/offlineTransactionsCsv",
        query: {
        startDate: formatDateTimeRange(startDate, "start"),
        endDate: formatDateTimeRange(endDate, "end"),
      },
        callback: (response) => {
          setExporting(false);
          if (response?.success) {
            toast.success(response.message || "Report generated successfully");
          } else {
            toast.error(response?.message || "Failed to generate report");
          }
        },
      }),
    );
  };

  const handleDownloadReport  = () => {
    const link = document.createElement("a");
    link.href = `${
      process.env.REACT_APP_API_BASE_URL
    }/static_data/${package_name}/offlineTransaction.xlsx?nocache=${Date.now()}`;
    link.download = "offlineTransaction.xlsx";
    link.click();
  };

  return (
    <Box sx={{ width: "98%" }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          mt: 0,
          mb: { xs: 5, md: 5 },
          background: "#fff",
          borderRadius: "3px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight={700} fontSize={24} p={2} fontFamily={"Aeonik"}>
          Offline Transaction
        </Typography>

        <Box display="flex" gap={1.5} px={2} pb={1} flexWrap="wrap" alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                gap={1.5}
                px={2}
                py={1}
              >
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  maxDate={endDate || new Date()}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        minWidth: 160,
                        "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                        "& .MuiInputLabel-root": { fontSize: 13 },
                      },
                    },
                  }}
                />
          
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        minWidth: 160,
                        "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                        "& .MuiInputLabel-root": { fontSize: 13 },
                      },
                    },
                  }}
                />
              </Box>
              <Button
                variant="outlined"
                fullWidth={isMobile}
                sx={{
                  height: 36,
                  minWidth: 120,
                  fontSize: 13,
                  textTransform: "none",
                  px: 2,
                  whiteSpace: "nowrap",
                }}
                  onClick={handleGetDetails}
              >
                  Get Details
              </Button>
            </LocalizationProvider>
        </Box>
        <CardContent
          sx={{
            height: "580px",
            width: "100%",
          }}
        >
          <TableCompo
            columns={columns}
            data={data}
            totalCount={total}
            page={page}
            limit={limit}
            onTableChange={handleTableChange}
            extraButtons={
              <Stack direction="row" spacing={2}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleExportReport}
                  disabled={exporting}
                >
                  {exporting ? "Exporting..." : "Export Report"}
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDownloadReport}
                >
                  Download
                </Button>
              </Stack>
            }
          />
        </CardContent>
      </Box>

      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Pending Transaction</DialogTitle>
        <DialogContent>
          Do you want to approve this pending transaction or cancel it?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="error">
            Cancel
          </Button>
          <Button onClick={handleApprove} color="success" variant="contained">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDialog}
        onClose={handleCancelDialogClose}
        PaperProps={{
          sx: {
            width: "600px",
            maxWidth: "800px",
          },
        }}
      >
        <DialogTitle>Cancel Transaction</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Enter your reason to cancel transaction"
            type="text"
            fullWidth
            value={cancelReason}
            onChange={(e) => {
              const regex = /^[A-Za-z\s]*$/;
              if (regex.test(e.target.value)) {
                setCancelReason(e.target.value);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose}>Cancel</Button>
          <Button
            onClick={handleCancelConfirm}
            variant="contained"
            color="error"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={imageDialogOpen}
        onClose={handleImageDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedImage ? (
            <Box
              component="img"
              src={selectedImage}
              alt="Offline transaction document"
              sx={{
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          ) : (
            <Typography>No image available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImageDownload} disabled={!selectedImage}>
            Download
          </Button>
          <Button onClick={handleImageDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfflineTransaction;
